import { useState } from 'react';
import cx from 'classnames';
import { useDispatch } from 'react-redux';

import { profileActions } from 'store/actions';
import { profileChannelIdSelector, profileChannelNameSelector } from 'store/selectors';
import { api, LoggerService, tracker } from 'services';
import { useStore } from 'hooks';

import PriceSelect from './SetMembershipPrice.PriceSelect';
import Success from './SetMembershipPrice.Success';

import styles from './SetMembershipPrice.module.scss';

export type SetMembershipPriceProps = {
  skippable?: boolean;
  onSkipped?(): void;
  onSucceeded?(): void;
};

export type StatusType = 'idle' | 'loading' | 'success' | 'fail';

const options = [
  { label: '$5', value: 5 },
  { label: '$10', value: 10 },
  { label: '$15', value: 15 }
];

const SetMembershipPrice = ({ skippable, onSucceeded, onSkipped }: SetMembershipPriceProps) => {
  const dispatch = useDispatch();
  const { channelId, channelName } = useStore(state => ({
    channelId: profileChannelIdSelector(state),
    channelName: profileChannelNameSelector(state)
  }));

  const [price, setPrice] = useState(5);
  const [status, setStatus] = useState<StatusType>('idle');
  const isSubmitting = status === 'loading';

  const handlePriceChange = value => setPrice(value);

  const handleOnSetPrice = async () => {
    try {
      setStatus('loading');
      tracker.setMembershipPrice(channelId, channelName, price);

      const response = await api.requests.ltv.setMembershipPrice(channelName, price);

      dispatch(profileActions.setMembershipPricingPlan(response.data?.membershipPricingPlan));

      tracker.setMembershipPriceSuccess(channelId, channelName, price);

      setStatus('success');
    } catch (error) {
      LoggerService.log('SET_CHANNEL_MEMBERSHIP_PRICE', error);

      if (error instanceof Error) {
        tracker.setMembershipPriceFail(channelId, channelName, error);
      }

      setStatus('fail');
    }
  };

  const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleOnSetPrice();
  };

  const renderContent = () => {
    if (status === 'success') {
      return <Success onProceed={onSucceeded} />;
    }

    return (
      <form className={styles.set} onSubmit={handleOnSubmit}>
        <div className={styles.content}>
          <h3 className={styles.title}>Get your content valuable</h3>
          <p className={styles.subtitle}>
            On Livetalks, creators earn a living doing what they love. Set up paid subscriptions in just a few minutes.
          </p>
          <PriceSelect className={styles.select} value={price} options={options} onChange={handlePriceChange} />
          <div className={styles.benefit}>
            <p>💡 Key benefit</p>
            <span>Creators keep 90% of their revenue minus credit card fees!</span>
          </div>
        </div>
        <div className={styles.actions}>
          <button type="submit" className={styles.action} disabled={isSubmitting} onClick={handleOnSetPrice}>
            {isSubmitting ? 'Loading... ' : 'Get started'}
          </button>
          {skippable && (
            <button type="button" className={cx(styles.action, styles.skip)} onClick={onSkipped}>
              Skip for now
            </button>
          )}
        </div>
      </form>
    );
  };

  return <div className={styles.container}>{renderContent()}</div>;
};

export default SetMembershipPrice;
